import React, { useEffect, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { eventService } from '../services/eventService';
import { subscriptionService, type CreditRefillStatus } from '../services/subscriptionService';
import { formatCreditRefillNotice } from '../utils/creditRefill';

import './CreditRefillNotice.css';

type PendingRefill = { promise: Promise<CreditRefillStatus>; next?: Promise<CreditRefillStatus> };
const pendingRequests = new Map<string, PendingRefill>();

function requestRefill(key: string): Promise<CreditRefillStatus> {
  // Coalesce here by subscription identity; the signal bypasses the transport's
  // URL-only coalescing so a changed plan cannot reuse its previous response.
  const request = subscriptionService.getNextCreditGrant(new globalThis.AbortController().signal).finally(() => {
    if (pendingRequests.get(key)?.promise === request) pendingRequests.delete(key);
  });

  pendingRequests.set(key, { promise: request });

  return request;
}

function loadRefill(key: string, force: boolean): Promise<CreditRefillStatus> {
  const existing = pendingRequests.get(key);

  if (!existing) return requestRefill(key);
  // Credit changes during a request require a fresh read after it settles.
  if (force && !existing.next) {
    existing.next = existing.promise.catch(() => undefined).then(() => requestRefill(key));
  }

  return existing.next || existing.promise;
}

function UserCreditRefillNotice({ requestKey, className }: { requestKey: string; className?: string }) {
  const [status, setStatus] = useState<CreditRefillStatus | null>(null);

  useEffect(() => {
    let active = true;
    let revision = 0;
    const refresh = (force = true) => {
      const current = ++revision;

      if (force) setStatus(null);
      void loadRefill(requestKey, force).then((value) => { if (active && current === revision) setStatus(value); })
        .catch(() => { if (active && current === revision) setStatus(null); });
    };

    refresh(false);
    const timer = window.setInterval(() => refresh(false), 60_000);
    const unsubscribe = eventService.subscribe('credits_update', () => refresh());

    return () => { active = false; window.clearInterval(timer); unsubscribe(); };
  }, [requestKey]);
  const message = status ? formatCreditRefillNotice(status) : null;

  return message ? <span className={`credit-refill-notice${className ? ` ${className}` : ''}`}>{message}</span> : null;
}

/** Only mounted at an active credit gate; never fetches during normal planning. */
export function CreditRefillNotice({ className }: {
  className?: string;
  projectId?: string | null;
}) {
  const { user } = useAuth();
  const key = `${user?.id}:${user?.subscription_tier}:${user?.subscription_status}`;

  return user?.id ? <UserCreditRefillNotice
    key={key} requestKey={key} className={className} /> : null;
}
